import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Atmosphere } from "../components/Atmosphere";
import { dictionaryInfo, dictionaryWords, chapterTranslations } from "../data/dictionary";
import { Search, Sparkles } from "lucide-react";

export default function Woerterbuch() {
  // Holt sich die Sprache direkt aus dem localStorage
  const [currentLang, setCurrentLang] = useState(() => {
    return localStorage.getItem("project_lang") || "de";
  });

  useEffect(() => {
    const handleLangChange = () => {
      setCurrentLang(localStorage.getItem("project_lang") || "de");
    };
    window.addEventListener("languageChange", handleLangChange);
    return () => window.removeEventListener("languageChange", handleLangChange);
  }, []);

  const [activeTab, setActiveTab] = useState("vocab"); 
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Alle");

  const words = dictionaryWords || [];
  const chapters = chapterTranslations || [];
  const info = dictionaryInfo || {
    name: "Nirakh Veshan",
    literalTranslation: { de: "Himmelssprache der Seele", en: "Heavenly Language of the Soul" },
    description: { de: "Die Sprache des Drachenvolkes.", en: "The language of the dragon people." }
  };

  // Kategorien dynamisch basierend auf der aktuellen Sprache generieren
  const categories = [
    "Alle", 
    ...new Set(words.map((item) => item.category?.[currentLang] || item.category?.de))
  ];

  // Suchbegriffe und Kategorien filtern
  const filteredWords = words.filter((item) => {
    const translationText = item.translation?.[currentLang] || "";
    const itemCategory = item.category?.[currentLang] || item.category?.de || "";
    
    const matchesSearch = 
      item.word?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      translationText.toLowerCase().includes(searchTerm.toLowerCase());
      
    const matchesCategory = selectedCategory === "Alle" || itemCategory === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="pt-32 pb-24 px-6 relative min-h-screen bg-[#080808]" data-testid="dictionary-page">
      <Atmosphere variant="minimal" />
      
      <div className="max-w-4xl mx-auto relative z-10">
        {/* Titel-Kopfzeile */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <p className="chinese text-[#C9A227]/70 text-2xl mb-2">語言</p>
          <h1 className="serif text-5xl lg:text-6xl text-[#E8DDC8] mb-4">{info.name}</h1>
          <p className="serif italic text-[#C9A227] text-lg mb-3">
            » {info.literalTranslation?.[currentLang]} «
          </p>
          {info.freeTranslation?.[currentLang] && (
            <p className="serif italic text-[#B3B6B7] text-sm mb-4">
              ({info.freeTranslation[currentLang]})
            </p>
          )}
          <p className="text-[#B3B6B7] text-sm max-w-xl mx-auto leading-relaxed">
            {info.description?.[currentLang]}
          </p>
        </motion.div>

        {/* Tab-Wechsler */}
        <div className="flex justify-center gap-4 mb-12 border-b border-[#C9A227]/15 pb-4">
          <button
            onClick={() => { setActiveTab("vocab"); setSearchTerm(""); setSelectedCategory("Alle"); }}
            className={`serif text-xl px-6 py-2 transition-all relative ${
              activeTab === "vocab" ? "text-[#C9A227]" : "text-[#7A7C7D] hover:text-[#E8DDC8]"
            }`}
          >
            {currentLang === "en" ? "Language Archive" : "Spracharchiv"}
            {activeTab === "vocab" && (
              <motion.div layoutId="activeTabLine" className="absolute bottom-[-17px] left-0 right-0 h-[2px] bg-[#C9A227]" />
            )}
          </button>
          <button
            onClick={() => { setActiveTab("chapters"); setSearchTerm(""); }}
            className={`serif text-xl px-6 py-2 transition-all relative ${
              activeTab === "chapters" ? "text-[#C9A227]" : "text-[#7A7C7D] hover:text-[#E8DDC8]"
            }`}
          >
            {currentLang === "en" ? "Chapter Translations" : "Kapitelübersetzungen"}
            {activeTab === "chapters" && (
              <motion.div layoutId="activeTabLine" className="absolute bottom-[-17px] left-0 right-0 h-[2px] bg-[#C9A227]" />
            )}
          </button>
        </div>

        {/* INHALT: WECHSELNDE TABS */}
        <AnimatePresence mode="wait">
          {activeTab === "vocab" && (
            <motion.div
              key="vocab-tab"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Filter & Suche */}
              <div className="flex flex-col md:flex-row gap-4 mb-8">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7A7C7D]" size={18} />
                  <input
                    type="text"
                    placeholder={currentLang === "en" ? "Search term or meaning..." : "Begriff oder Bedeutung suchen..."}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-[#121212] border border-[#C9A227]/20 rounded p-3 pl-12 text-[#E8DDC8] focus:outline-none focus:border-[#C9A227] transition-colors text-sm"
                  />
                </div>
                <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 scrollbar-none">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      className={`px-4 py-2 text-[10px] uppercase tracking-widest border transition-all rounded whitespace-nowrap ${
                        selectedCategory === cat
                          ? "border-[#C9A227] bg-[#C9A227]/10 text-[#C9A227]"
                          : "border-[#C9A227]/15 text-[#7A7C7D] hover:text-[#E8DDC8]"
                      }`}
                    >
                      {cat === "Alle" && currentLang === "en" ? "All" : cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* SPRACHARCHIV ALS LISTE */}
              <div className="card-imperial p-2 bg-[#0a0a0a]/60 divide-y divide-[#C9A227]/10">
                {filteredWords.length > 0 ? (
                  filteredWords.map((item) => (
                    <div key={item.word} className="p-4 flex flex-col sm:flex-row justify-between sm:items-center gap-2 hover:bg-[#C9A227]/5 transition-colors duration-150">
                      <div className="flex-1">
                        <div className="flex items-baseline gap-2">
                          <span className="serif text-xl text-[#E8DDC8] tracking-wide">{item.word}</span>
                          {item.pronunciation && (
                            <span className="text-xs italic text-[#7A7C7D]">[{item.pronunciation}]</span>
                          )}
                          <span className="text-[9px] uppercase tracking-widest text-[#7A7C7D] bg-[#161616] px-1.5 py-0.5 rounded border border-[#C9A227]/5 ml-2">
                            {item.category?.[currentLang] || item.category?.de}
                          </span>
                        </div>
                        {item.description?.[currentLang] && (
                          <p className="text-xs text-[#B3B6B7] mt-1 leading-relaxed max-w-xl">{item.description[currentLang]}</p>
                        )}
                      </div>
                      <div className="sm:text-right border-t sm:border-t-0 pt-2 sm:pt-0 border-[#C9A227]/5 sm:min-w-[180px]">
                        <span className="serif text-base text-[#C9A227]">{item.translation?.[currentLang]}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-[#7A7C7D] py-8 italic">
                    {currentLang === "en" ? "No imperial terms found." : "Keine imperialen Begriffe gefunden."}
                  </p>
                )}
              </div>
            </motion.div>
          )}

          {/* INHALT: COMPACT KAPITELÜBERSETZUNGEN */}
          {activeTab === "chapters" && (
            <motion.div
              key="chapters-tab"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-12"
            >
              {chapters.map((chap, cIndex) => (
                <div key={cIndex} className="card-imperial overflow-hidden bg-[#0a0a0a]">
                  {/* Kapitel Header */}
                  <div className="p-5 bg-gradient-to-r from-[#14120c] to-[#0a0a0a] border-b border-[#C9A227]/15 flex items-center justify-between">
                    <h2 className="serif text-2xl text-[#C9A227]">
                      {chap.chapter?.[currentLang] || chap.chapter?.de}
                    </h2>
                    <span className="text-[10px] uppercase tracking-widest text-[#7A7C7D]">
                      {chap.phrases?.length || 0} {currentLang === "en" ? "Fragments" : "Fragmente"}
                    </span>
                  </div>

                  {/* Alle Zeilen gesammelt in EINEM Kasten untereinander */}
                  <div className="divide-y divide-[#C9A227]/10">
                    {chap.phrases?.map((phrase, pIndex) => (
                      <div key={pIndex} className="p-6 grid md:grid-cols-2 gap-4 hover:bg-[#C9A227]/5 transition-colors duration-150">
                        <div>
                          <span className="text-[9px] uppercase tracking-widest text-[#7A7C7D] block mb-1">
                            {currentLang === "en" ? "Original Line" : "Originalzeile"}
                          </span>
                          <p className="serif text-base italic text-[#E8DDC8] tracking-wide leading-relaxed">
                            » {phrase.original} «
                          </p>
                        </div>
                        <div className="md:pl-4 md:border-l md:border-l-[#C9A227]/10 flex flex-col justify-between">
                          <div>
                            <span className="text-[9px] uppercase tracking-widest text-[#C9A227] block mb-1">
                              {currentLang === "en" ? "Translation" : "Übersetzung"}
                            </span>
                            <p className="serif text-base text-[#C9A227] leading-relaxed">
                              {phrase.translation?.[currentLang]}
                            </p>
                          </div>
                          {phrase.context?.[currentLang] && (
                            <p className="text-[11px] text-[#7A7C7D] mt-2 italic flex items-center gap-1">
                              <Sparkles size={10} className="text-[#C9A227]/70" /> {phrase.context[currentLang]}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}